const FOCUS_POLL_INTERVAL_MS = 100;

// iOS Safari may not surface any pointer or mouse event on the parent for a tap inside a
// cross-origin iframe, so the pointer guard would swallow every real click there. Left off
// until verified on a device.
const isIosDevice = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export interface DetectIframeClicksOptions {
  iframe: HTMLIFrameElement;
  onClick: () => void;
  /**
   * The top accessible window: it is primed with focus, reclaims it after each detection, and is
   * where blur is listened for — browsers never dispatch blur to subframe windows, so a window
   * that sits in an iframe only sees the click through an ancestor it can reach.
   */
  focusWindow?: Window;
}

/**
 * Counts a click on a creative iframe, which cannot receive DOM click events across origins, by
 * watching for focus moving into it (the legacy `Eic` renderer's heuristic).
 *
 * - A window only receives blur if it held focus first, so it is primed (only when nothing in it
 *   holds focus, to leave a publisher input or another frame alone).
 * - `activeElement` is read one tick after blur, once focus has finished moving.
 * - A creative that grabs focus on its own (autofocus, `focus()` on load) moves focus into the
 *   iframe with no pointer near it. The parent sees mouseenter/mouseleave on the iframe element
 *   even over cross-origin content, so focus only counts as a click while the pointer is over it.
 *   Either way focus is handed back to the window, so the next real click blurs it again.
 * - Firefox never dispatches blur to the top window when focus enters a child frame, and WebKit
 *   refuses `focus()` from a cross-origin subframe, so while the pointer is over the iframe
 *   `activeElement` is also polled for a focus move blur never announced.
 *
 * Keyboard focus counts only while the pointer rests over the iframe.
 */
export const detectIframeClicks = ({
  iframe,
  onClick,
  focusWindow = window
}: DetectIframeClicksOptions): (() => void) => {
  const ownDocument = iframe.ownerDocument;
  const isPointerGuardEnabled = !isIosDevice();

  // seeded from :hover so a re-arm while the pointer already rests on the iframe doesn't lose
  // the state: mouseenter won't fire again
  let isPointerOver = iframe.matches(':hover');
  let isIframeActive = false;
  let checkTimeoutId: ReturnType<typeof setTimeout> | undefined;
  let pollIntervalId: ReturnType<typeof setInterval> | undefined;

  const checkFocus = (isFocusChange: boolean) => {
    const isActive = ownDocument.activeElement === iframe;
    if (isActive && (isFocusChange || !isIframeActive)) {
      if (isPointerOver || !isPointerGuardEnabled) onClick();
      focusWindow.focus();
    }
    isIframeActive = ownDocument.activeElement === iframe;
  };

  const onBlur = () => {
    if (checkTimeoutId !== undefined) clearTimeout(checkTimeoutId);
    checkTimeoutId = setTimeout(() => checkFocus(true));
  };

  const startPolling = () => {
    pollIntervalId ??= setInterval(() => checkFocus(false), FOCUS_POLL_INTERVAL_MS);
  };
  const stopPolling = () => {
    if (pollIntervalId === undefined) return;
    clearInterval(pollIntervalId);
    pollIntervalId = undefined;
  };

  const onPointerEnter = () => {
    isPointerOver = true;
    startPolling();
  };
  const onPointerLeave = () => {
    // a quick click-and-leave can land between two polls
    checkFocus(false);
    isPointerOver = false;
    stopPolling();
  };

  const primeFocus = () => {
    const { activeElement, body } = focusWindow.document;
    if (!activeElement || activeElement === body) focusWindow.focus();
  };
  const primeFocusWhenVisible = () => {
    if (ownDocument.visibilityState === 'visible') primeFocus();
  };

  primeFocus();
  if (isPointerOver) startPolling();
  iframe.addEventListener('mouseenter', onPointerEnter);
  iframe.addEventListener('mouseleave', onPointerLeave);
  focusWindow.addEventListener('blur', onBlur);
  ownDocument.addEventListener('visibilitychange', primeFocusWhenVisible);

  return () => {
    if (checkTimeoutId !== undefined) clearTimeout(checkTimeoutId);
    stopPolling();
    iframe.removeEventListener('mouseenter', onPointerEnter);
    iframe.removeEventListener('mouseleave', onPointerLeave);
    focusWindow.removeEventListener('blur', onBlur);
    ownDocument.removeEventListener('visibilitychange', primeFocusWhenVisible);
  };
};
